import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import Header from './Header';
import { AppShellProvider, useAppShell } from './AppShellContext';
import CaptureSheet from './CaptureSheet';
import SearchSheet from './SearchSheet';
import Fab from './Fab';
import OnlineIndicator from './OnlineIndicator';
import LogForm from './LogForm';
import PhotoUploadSheet from './PhotoUploadSheet';

/**
 * Two compositions in one shell:
 *
 *   Mobile (`< md`): sticky header (brand · season · overflow menu),
 *     page content (full-width, with bottom padding equal to bottom-
 *     nav height), bottom nav at the foot of the viewport. Capture
 *     and Search sheets slide up from the bottom; FAB hovers above
 *     the bottom nav when a page has registered a primary action.
 *
 *   Desktop (`md` and up): same header, two-column body with the
 *     left rail showing the full route inventory, the bottom nav and
 *     FAB hidden.
 *
 * The provider hosts the bits of cross-page state (capture sheet,
 * search sheet, FAB action) that any descendant page can mutate.
 */
export default function AppShell() {
  return (
    <AppShellProvider>
      <div className="min-h-screen flex flex-col">
        <Header />

        <div className="container flex-1 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 py-6">
          <aside className="hidden md:block">
            <Sidebar />
          </aside>
          <main
            className="min-w-0"
            // Reserve room for the mobile bottom nav (≈64px tall plus
            // safe-area). On desktop this padding is harmless; the
            // sticky bottom nav is hidden there.
            style={{ paddingBottom: 'calc(76px + env(safe-area-inset-bottom))' }}
          >
            <Outlet />
          </main>
        </div>

        <OnlineIndicator />
        <Fab />
        <BottomNav />
        <CaptureSheet />
        <SearchSheet />
        <GlobalLogForm />
        <GlobalPhotoSheet />
      </div>
    </AppShellProvider>
  );
}

function GlobalLogForm() {
  const { logFormOpen, logIntent, closeLogForm } = useAppShell();
  return (
    <LogForm
      open={logFormOpen}
      onOpenChange={(o) => (o ? null : closeLogForm())}
      mode="create"
      defaults={logIntent ?? undefined}
    />
  );
}

function GlobalPhotoSheet() {
  const { photoSheetOpen, photoIntent, closePhotoSheet } = useAppShell();
  return (
    <PhotoUploadSheet
      open={photoSheetOpen}
      onOpenChange={(o) => (o ? null : closePhotoSheet())}
      defaults={photoIntent ?? undefined}
    />
  );
}
